import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService.js';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

export const WidgetEnum = {
	MyDay: 101,
	MyArea: 102,
	DetentionRate: 103,
	ShareOfShelf: 104,
	OOSRate: 105,
	TotalMissingProduct: 106,
	SPS: 107,
	OrderTracking: 108,
};

const widgetCache = new CacheManager('VueCache', 'cache', {
	maxAge: 7 * 24 * 60 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'Widget',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.widgets.`,
});

export async function getWidgets() {
	const cacheKey = 'widgets';

	const memCached = widgetCache.getFromMemory(cacheKey);
	if (memCached) {
		return memCached;
	}

	try {
		const legacyData = await iframeDataBridge.getStorageAsync('ORGAWIDGETS');
		if (legacyData) {
			log('info', 'Widget', '📦 Seeding Vue cache from legacy storage (ORGAWIDGETS)');
			await widgetCache.put(cacheKey, legacyData);
			widgetCache.setInMemory(cacheKey, legacyData);
			return legacyData;
		}
	} catch (legacyError) {
		log('warn', 'Widget', `⚠️ Legacy storage check failed: ${legacyError.message}`);
	}

	try {
		const cachedWidgets = await widgetCache.get(cacheKey);
		if (cachedWidgets) {
			widgetCache.setInMemory(cacheKey, cachedWidgets);

			if (!iframeDataBridge.isOnline()) {
				log('info', 'Widget', '📴 Offline: Returning cached widgets');
				return cachedWidgets;
			}

			setTimeout(() => refreshWidgetsInBackground(), 100);
			return cachedWidgets;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Widget', '📴 Offline: No cached widgets available');
			return [];
		}

		return await fetchWidgetsFromAPI();
	} catch (error) {
		log('error', 'Widget', `❌ Error getting widgets: ${error.message}`);

		try {
			const fallbackWidgets = await widgetCache.get(cacheKey);
			if (fallbackWidgets) {
				log('info', 'Widget', '💾 Using fallback cached widgets');
				return fallbackWidgets;
			}
		} catch (cacheError) {
			log('error', 'Widget', `❌ Cache fallback failed: ${cacheError.message}`);
		}

		return [];
	}
}

async function fetchWidgetsFromAPI() {
	try {
		const apiUrl = getApiUrl(`Core/OrganisationWidgets/User`);
		const response = await authenticatedFetch(
			apiUrl,
			{
				method: 'GET',
			},
			'tokenSales'
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const widgets = await response.json();
		const cacheKey = 'widgets';
		await widgetCache.put(cacheKey, widgets);
		widgetCache.setInMemory(cacheKey, widgets);

		log('success', 'Widget', `✅ Fetched and cached ${widgets.length} widgets`);
		return widgets;
	} catch (error) {
		log('error', 'Widget', `❌ Failed to fetch widgets from API: ${error.message}`);
		throw error;
	}
}

async function refreshWidgetsInBackground() {
	try {
		await fetchWidgetsFromAPI();
		log('success', 'Widget', '🔄 Background refresh of widgets completed');
	} catch (error) {
		log('warn', 'Widget', `⚠️ Background refresh failed: ${error.message}`);
	}
}

export async function refreshWidgets() {
	const cacheKey = 'widgets';

	widgetCache.deleteFromMemory(cacheKey);
	await widgetCache.delete(cacheKey);
}

export async function clearWidgetCache() {
	await widgetCache.clearAll();
}

export async function getWidgetCacheStats() {
	return await widgetCache.getStats();
}

export async function cleanOldWidgetCache(maxAgeInDays = 7) {
	await widgetCache.cleanOld(maxAgeInDays);
}

cleanOldWidgetCache();
